// Fachsprache und Aufrufe der Adresspflege (ST-PTY-002, zweiter Schnitt;
// CM-PTY-006; ADR-0056).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// contact-points.ts und identifiers.ts: was hier steht, ist ohne Browser
// pruefbar.
//
// DIE WOERTER "USAGE", "RAW" UND "NORMALIZATION" KOMMEN AUF DEM BILDSCHIRM NICHT
// VOR (NFR-UX-003, CM-UX-010). Eine Verwendung heisst dort "Zweck", und der
// Pruefstatus 'raw' heisst "nicht geprüft"; die Pille dazu steht erst, wenn ein
// anderer Status moeglich ist (pruefPilleSichtbar).

import type { PlainMessage } from './access.ts';
import { readResource, sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';
import { END_REASON_LABELS, END_REASON_MISSING, OFFERED_END_REASONS } from './contact-points.ts';
import { partyHref } from './parties.ts';

export type { SendLike } from './command-client.ts';

/** Die Adresse der Adresspflege. Neben den anderen Pflegebildschirmen des Kontakts. */
export function addressesHref(partyId: string): string {
  return `${partyHref(partyId)}/adressen`;
}

/**
 * Die Zwecke, die dieser Bildschirm anbietet -- die vier, die der Server
 * annimmt (A3 vom 28.09.2026). `private` fehlt, bis die Policy nach
 * CM-GEO-013 steht; apps/web/test/addresses.test.ts haelt die Liste gegen die
 * Konstante des Servers.
 *
 * KEINE VORBELEGUNG. Der haeufigste Zweck ist vermutlich die
 * Geschaeftsanschrift -- eine vorbelegte Auswahl machte aber aus einer
 * vergessenen Entscheidung eine Aussage ("die Rechnung geht dorthin"), die
 * niemand getroffen hat. Der Server verlangt den Zweck ohne Standard.
 */
export const OFFERED_USAGES: readonly { readonly code: string; readonly label: string; readonly hint: string }[] =
  Object.freeze([
    Object.freeze({
      code: 'business',
      label: 'Geschäftsanschrift',
      hint: 'Die Anschrift, unter der dieser Kontakt geschäftlich erreichbar ist.',
    }),
    Object.freeze({
      code: 'billing',
      label: 'Rechnungsanschrift',
      hint: 'Hierhin gehen Rechnungen und Buchhaltungspost.',
    }),
    Object.freeze({
      code: 'delivery',
      label: 'Lieferanschrift',
      hint: 'Hierhin gehen Warensendungen. Nach einer einmaligen Lieferung setzt du sie wieder auf „Nicht mehr verwenden“.',
    }),
    Object.freeze({
      code: 'review_copy',
      label: 'Presse- und Rezensionsexemplare',
      hint: 'Hierhin gehen Leseexemplare und Presseexemplare.',
    }),
  ]);

/**
 * Fachbegriffe der Zwecke -- fuer Anzeige und Historie, WEITER als die
 * Auswahl: eine Zeile kann einen Zweck tragen, den dieser Bildschirm nicht
 * anbietet (`private` aus einem spaeteren Import). Ein unuebersetzter Code
 * waere ein Technikbegriff auf dem Bildschirm.
 */
export const USAGE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  ...Object.fromEntries(OFFERED_USAGES.map((eintrag) => [eintrag.code, eintrag.label])),
  private: 'Privatanschrift',
});

export function usageLabel(code: string): string {
  return USAGE_LABELS[code] ?? code;
}

/**
 * Der Pruefstatus in Worten (A1). Nur 'raw' entsteht heute; die anderen Werte
 * stehen im CHECK aus 0014 und bekommen trotzdem einen Satz, damit eine
 * spaetere Normalisierung hier keinen Technikbegriff zeigt.
 */
export const NORMALIZATION_LABELS: Readonly<Record<string, string>> = Object.freeze({
  // Angezeigt erst, wenn ein anderer Status möglich ist (pruefPilleSichtbar).
  raw: 'nicht geprüft',
  normalized: 'automatisch vereinheitlicht',
  confirmed: 'geprüft',
  ambiguous: 'mehrdeutig',
  rejected: 'nicht zustellbar',
});

export function normalizationLabel(code: string): string {
  return NORMALIZATION_LABELS[code] ?? 'Prüfstand unbekannt';
}

/**
 * Ob die Prüfpille an einer Anschrift steht: `raw` trägt heute
 * jede Anschrift, und niemand kann es ändern -- eine Pille, die überall gleich
 * ist, sagt nichts. Jeder andere Status zeigt sie.
 */
export function pruefPilleSichtbar(code: string): boolean {
  return code !== 'raw';
}

/**
 * Der eine Satz, dass LIAISON Anschriften nicht prueft -- EINMAL am Formular
 * und nicht an jeder Zeile (5.2 Nr. 14, Calm technology).
 */
export const UNCHECKED_EXPLANATION =
  'Anschriften werden so gespeichert, wie du sie eingibst. LIAISON prüft sie noch nicht auf Schreibweise oder Zustellbarkeit.';

/**
 * Die Endgruende -- DIESELBEN wie bei den Kontaktwegen. `superseded` heisst
 * dort "Umzug, neue Anschrift", und genau das ist der Regelfall einer Anschrift.
 * Nur die Hinweise sind auf Anschriften zugeschnitten; die Codes und ihre
 * Bindung an `party.end_reason` (Probe in contact-points.test.ts) bleiben
 * eine Stelle.
 */
export const OFFERED_ADDRESS_END_REASONS: readonly { readonly code: string; readonly label: string; readonly hint: string }[] =
  Object.freeze(
    OFFERED_END_REASONS.map((eintrag) =>
      Object.freeze({
        ...eintrag,
        hint:
          eintrag.code === 'superseded'
            ? 'Umzug oder neue Anschrift. Trage die neue Anschrift danach als eigene Anschrift ein.'
            : 'Der Zweck gilt nicht mehr, etwa nach einer einmaligen Lieferung oder einem beendeten Vertrag.',
      }),
    ),
  );

export function endReasonLabel(code: string | undefined): string {
  if (code === undefined || code.length === 0) {
    return END_REASON_MISSING;
  }
  return END_REASON_LABELS[code] ?? code;
}

/** Das Land, fuer das die Postleitzahl Pflicht ist -- derselbe Wert wie im Server (A2). */
export const POSTAL_CODE_REQUIRED_COUNTRY = 'DE';

/** Die Vorbelegung des Landes. Hier SCHON: ein Land ist keine Aussage ueber einen Zweck. */
export const DEFAULT_COUNTRY = 'DE';

/**
 * ISO 3166-1 alpha-2 -- DIESELBE Liste wie ISO_3166_1_ALPHA_2 im Server,
 * gehalten durch apps/web/test/addresses.test.ts. Eine eigene Kopie und kein
 * Import, weil das Web-Paket den Servercode nicht buendeln darf.
 */
export const COUNTRY_CODES: readonly string[] = Object.freeze([
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
]);

let regionNames: Intl.DisplayNames | undefined;

/**
 * Der deutsche Name eines Landes. `Intl.DisplayNames` ist hier NUR die
 * Beschriftung, nie die Pruefung (die geschlossene Liste oben entscheidet).
 * Rueckfall ist der Code selbst -- ehrlicher als ein leeres Feld.
 */
export function countryName(code: string): string {
  try {
    regionNames ??= new Intl.DisplayNames(['de'], { type: 'region' });
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Die Auswahl des Landes, nach deutschem Namen sortiert. */
export function countryOptions(): readonly { readonly code: string; readonly name: string }[] {
  return COUNTRY_CODES.map((code) => ({ code, name: countryName(code) })).sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

/**
 * Die Lage einer Anschrift (ST-GEO-001 G1): ein geschlossenes Vokabular des
 * Servers. Fehlt es, wird fuer die Anschrift keine Lage ermittelt, und der
 * Bildschirm zeigt nichts dazu.
 */
export type AddressLocationStatus = 'matched' | 'ambiguous' | 'not_found' | 'pending' | 'failed';

/** Wortlaut von DESIGN (14.10.2026): Begriff "Lage"; warnen nur, wo die Nutzerin etwas pruefen kann oder wissen muss. */
export const ADDRESS_LOCATION_TEXT: Readonly<
  Record<AddressLocationStatus, { readonly label: string; readonly description?: string; readonly warning: boolean }>
> = Object.freeze({
  matched: { label: 'Lage gefunden', warning: false },
  ambiguous: { label: 'Lage mehrdeutig', description: 'Mehrere Stellen passen. Prüfe Straße und Postleitzahl.', warning: true },
  not_found: { label: 'Lage nicht gefunden', description: 'Prüfe die Schreibweise von Straße und Ort.', warning: true },
  pending: { label: 'Lage wird ermittelt', warning: false },
  failed: {
    label: 'Lage nicht ermittelt',
    description: 'Der Kartendienst war mehrmals nicht erreichbar. Ein neuer Versuch startet, wenn du die Anschrift korrigierst.',
    warning: true,
  },
});

/** Ein unbekannter Wert eines neueren Servers zeigt nichts, statt etwas Falsches. */
function locationOf(value: unknown): { readonly locationStatus?: AddressLocationStatus } {
  return typeof value === 'string' && Object.hasOwn(ADDRESS_LOCATION_TEXT, value) ? { locationStatus: value as AddressLocationStatus } : {};
}

export interface AddressView {
  readonly addressUsageId: string;
  /** Die Anschrift hinter der Verwendung; gleiche Kennung heisst dieselbe Anschrift. */
  readonly addressId: string;
  /** Sperrstand der ANSCHRIFT; "Anschrift korrigieren" schickt ihn mit. */
  readonly addressLockVersion: number;
  /** PLZ und Ort in eigenen Feldern -- sie fuellen die Korrektur vor. */
  readonly postalCode: string;
  readonly locality: string;
  readonly usageCode: string;
  readonly isPreferred: boolean;
  readonly countryCode: string;
  readonly lines: readonly string[];
  readonly normalizationStatus: string;
  readonly locationStatus?: AddressLocationStatus;
  readonly sourceSystem: string;
  readonly validFrom: string;
  readonly lockVersion: number;
}

export interface EndedAddressView {
  readonly addressUsageId: string;
  readonly usageCode: string;
  readonly countryCode: string;
  readonly lines: readonly string[];
  readonly endedAt: string;
  readonly endReasonCode?: string;
}

export interface PartyAddresses {
  /** Der Anzeigename fuer Brotkrume und h1 (Gruppe `names`); fehlt er (aelteres Image), bleibt "Kontakt". */
  readonly displayName?: string;
  readonly postalAddresses: readonly AddressView[];
  readonly endedPostalAddresses: readonly EndedAddressView[];
  readonly additionalPostalAddressCount: number;
  /** Der Lebenszyklus des Kontakts; fehlt er (aelteres Image), bleibt er unbekannt und das Angebot steht. */
  readonly lifecycleStatus?: string;
  readonly endedPostalAddressesTruncated: boolean;
}

export type AddressesResult =
  | { readonly outcome: 'loaded'; readonly data: PartyAddresses }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type CommandResult = CommandOutcome<{ readonly usageCode: string; readonly lockVersion: number }>;

/**
 * Eine Anschrift mit ihren offenen Zwecken. Die Liste des Servers fuehrt je
 * Verwendung eine Zeile; der Bildschirm zeigt je Anschrift eine, damit
 * "Rechnung und Lieferung an dieselbe Anschrift" nicht wie zwei Anschriften
 * aussieht. Gruppiert wird nach der Kennung der Anschrift, nie nach ihrem
 * Wortlaut: zwei gleich geschriebene Anschriften sind ohne Pruefung nicht
 * dieselbe (ADR-0056 Ziffer 3).
 */
export interface AddressGroup {
  readonly addressId: string;
  readonly lines: readonly string[];
  readonly countryCode: string;
  readonly normalizationStatus: string;
  /** Je Anschrift eine Lage: alle Zwecke zeigen auf dieselbe Fassung. */
  readonly locationStatus?: AddressLocationStatus;
  readonly addressLockVersion: number;
  readonly postalCode: string;
  readonly locality: string;
  /** In der Reihenfolge des Servers; mindestens eine. */
  readonly usages: readonly AddressView[];
}

export function groupAddresses(addresses: readonly AddressView[]): readonly AddressGroup[] {
  const groups = new Map<string, { first: AddressView; usages: AddressView[] }>();
  for (const address of addresses) {
    const group = groups.get(address.addressId);
    if (group === undefined) {
      groups.set(address.addressId, { first: address, usages: [address] });
    } else {
      group.usages.push(address);
    }
  }
  return [...groups.values()].map(({ first, usages }) => ({
    addressId: first.addressId,
    lines: first.lines,
    countryCode: first.countryCode,
    normalizationStatus: first.normalizationStatus,
    ...(first.locationStatus === undefined ? {} : { locationStatus: first.locationStatus }),
    addressLockVersion: first.addressLockVersion,
    postalCode: first.postalCode,
    locality: first.locality,
    usages,
  }));
}

/** Die Ueberschrift einer Anschrift: ihr Zweck, bei mehreren "Zwecke: ...". */
export function usagesHeading(group: AddressGroup): string {
  const labels = group.usages.map((usage) => usageLabel(usage.usageCode));
  return labels.length === 1 ? (labels[0] ?? '') : `Zwecke: ${labels.join(', ')}`;
}

/** Die Zwecke, die diese Anschrift noch nicht hat -- nur sie bietet "Auch verwenden als" an. */
export function remainingUsages(group: AddressGroup): typeof OFFERED_USAGES {
  const taken = new Set(group.usages.map((usage) => usage.usageCode));
  return OFFERED_USAGES.filter((usage) => !taken.has(usage.code));
}

export const CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Jemand anderes war schneller.',
  explanation: 'Diese Anschrift hat sich geändert, seit du diese Seite geöffnet hast. Deine Eingabe wurde nicht gespeichert.',
  nextStep: 'Lade die Anschriften neu und entscheide dann noch einmal.',
  retryable: false,
});

export const ALREADY_ENDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Anschrift wird bereits nicht mehr verwendet.',
  explanation: 'Eine andere Person hat sie inzwischen auf „Nicht mehr verwenden“ gesetzt. Ein zweites Mal geht das nicht.',
  nextStep: 'Lade die Anschriften neu; sie steht dann unter „Frühere Anschriften“.',
  retryable: false,
  // Der gewollte Zustand gilt schon: ein Hinweis, kein Fehler.
  notice: true,
});

export const NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lässt sich keine Anschrift mehr eintragen.',
  // Der Satz nennt die Lage und nicht den Status-Code: anonymisiert,
  // zusammengefuehrt oder archiviert -- in allen drei Faellen waere eine neue
  // Angabe hier falsch (TM-017).
  explanation: 'Der Kontakt ist archiviert, anonymisiert oder mit einem anderen zusammengeführt.',
  nextStep: 'Geh zurück zum Kontakt und prüfe seinen Stand.',
  retryable: false,
});

/**
 * Die Verbindung riss beim HINZUFUEGEN ab. Ob die Anschrift gespeichert wurde,
 * weiss die Oberflaeche nicht -- und sie muss es nicht mehr wissen.
 *
 * BIS ZU ADR-0060 stand hier ein Satz mit `retryable: false`, der vor jeder
 * Wiederholung den Blick in die Liste verlangte: der Befehl trug keine
 * Kennung des Absendens, und ein zweiter Klick haette eine Dublette angelegt
 * (Befund des Codex-Cloud-Reviews an #268). Jetzt traegt die Wiederholung
 * dieselbe Kennung, und der Server antwortet mit dem Original statt einer
 * zweiten Zeile. Deshalb `retryable: true`, und der Satz nennt die Zusage.
 */
export const SUBMIT_FAILED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Das Absenden ist fehlgeschlagen.',
  explanation:
    'Die Verbindung ist abgebrochen, bevor LIAISON geantwortet hat. Ob die Anschrift schon gespeichert ist, ist offen. Deine Eingabe steht noch im Formular.',
  nextStep: 'Wähle „Erneut versuchen“. Ist die Anschrift schon angekommen, legt LIAISON sie kein zweites Mal an.',
  retryable: true,
});

/** Der erste Versuch kam mit anderen Angaben an; die jetzige Eingabe bleibt stehen. */
export const REQUEST_REUSED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dein erster Versuch ist mit anderen Angaben angekommen.',
  explanation: 'Deine jetzige Eingabe wurde nicht gespeichert. Sie steht noch im Formular.',
  nextStep: 'Lade die Anschriften neu und prüfe, ob die Anschrift schon da ist. Fehlt sie, füge sie danach hinzu.',
  retryable: false,
});

/**
 * Der erste Versuch ist angekommen, die Anschrift wurde seither geaendert oder
 * beendet. Das Formular wird geleert wie nach einem Erfolg -- sonst legte der
 * naechste Klick mit neuer Kennung eine zweite an.
 */
export const REQUEST_SUPERSEDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Anschrift ist schon angelegt und wurde inzwischen geändert.',
  explanation:
    'Dein erster Versuch ist angekommen, das Formular ist deshalb wieder leer. Danach hat jemand die Anschrift geändert oder auf „Nicht mehr verwenden“ gesetzt; ein weiterer Versuch ändert daran nichts.',
  nextStep: 'Lade die Anschriften neu und sieh nach, was jetzt gilt.',
  retryable: false,
});

/**
 * "Auch verwenden als" legt keine Anschrift an, sondern einen Zweck an einer
 * vorhandenen. Die Saetze des Hinzufuegens fragten sonst, ob "die Anschrift"
 * gespeichert ist, und versprachen eine Eingabe im Formular, die hier nicht
 * gemeint ist.
 */
export const USAGE_SUBMIT_FAILED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Das Absenden ist fehlgeschlagen.',
  explanation: 'Die Verbindung ist abgebrochen, bevor LIAISON geantwortet hat. Ob die Anschrift schon für den weiteren Zweck gilt, ist offen.',
  nextStep: 'Wähle „Erneut versuchen“. Ist der Zweck schon angekommen, trägt LIAISON ihn kein zweites Mal ein.',
  retryable: true,
});

/** Wie REQUEST_SUPERSEDED_MESSAGE, fuer einen weiteren Zweck; die Auswahl schliesst sich wie nach einem Erfolg. */
export const USAGE_SUPERSEDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Zweck ist schon eingetragen und wurde inzwischen geändert.',
  explanation:
    'Dein erster Versuch ist angekommen, die Auswahl ist deshalb geschlossen. Danach hat jemand den Zweck geändert oder auf „Nicht mehr verwenden“ gesetzt; ein weiterer Versuch ändert daran nichts.',
  nextStep: 'Lade die Anschriften neu und sieh nach, was jetzt gilt.',
  retryable: false,
});

/**
 * Die Felder einer Anschrift zum Korrigieren, aus der gespeicherten Fassung.
 * Die Druckzeilen sind Adresszeile 1, gegebenenfalls 2, dann "PLZ Ort" --
 * die letzte ist deshalb nicht Teil der Strassenangaben.
 */
export function correctionInputOf(group: AddressGroup): Omit<AddressInput, 'usageCode'> {
  const strassen = group.lines.slice(0, -1);
  return {
    countryCode: group.countryCode,
    addressLine1: strassen[0] ?? '',
    addressLine2: strassen.length > 1 ? strassen.slice(1).join(' ') : '',
    postalCode: group.postalCode,
    locality: group.locality,
  };
}

/** Die Anschrift wurde seit dem Laden anders korrigiert. */
export const ADDRESS_OUTDATED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Anschrift wurde inzwischen anders korrigiert.',
  explanation: 'Deine Korrektur wurde nicht gespeichert, damit die andere nicht still überschrieben wird.',
  nextStep: 'Lade die Anschriften neu und korrigiere dann noch einmal. Deine Eingabe geht dabei verloren; sieh dir vorher die jetzige Anschrift an.',
  retryable: false,
});

/** An der Anschrift haengt eine Verwendung, die diese Korrektur nicht mitaendern darf. */
export const NOT_CORRECTABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Anschrift lässt sich hier nicht korrigieren.',
  explanation: 'Sie wird auch an einer Stelle verwendet, die du von hier aus nicht mitändern darfst. Es wurde nichts geändert.',
  nextStep: 'Setze die falsche Anschrift auf „Nicht mehr verwenden“ und trage die richtige als neue Anschrift ein.',
  retryable: false,
});

/**
 * Der Zweck ist an dieser Anschrift schon eingetragen, meist von jemand
 * anderem in der Zwischenzeit. Doppelt wird er nicht angelegt; Neuladen zeigt
 * ihn.
 */
export function duplicateUsageMessage(usageCode: string): PlainMessage {
  return Object.freeze({
    headline: `Diese Anschrift ist schon als „${usageLabel(usageCode)}“ verwendet.`,
    explanation: 'Der Zweck wurde inzwischen eingetragen. Ein zweites Mal legt LIAISON ihn nicht an.',
    nextStep: 'Lade die Anschriften neu; der Zweck steht dann an der Anschrift.',
    retryable: false,
  });
}

export const NOT_FOUND_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Anschrift gibt es hier nicht.',
  explanation: 'Sie wurde entfernt, oder der Link ist nicht mehr gültig.',
  nextStep: 'Geh zurück zum Kontakt und öffne die Anschriften dort erneut.',
  retryable: false,
});

export const END_REASON_UNAVAILABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Grund lässt sich hier nicht auswählen.',
  explanation: 'Für ihn verlangt LIAISON einen Nachweis oder eine benannte Quelle, und beides gibt es in dieser Ansicht noch nicht.',
  nextStep: 'Wähle einen der angebotenen Gründe.',
  retryable: false,
});


function linesOf(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((line): line is string => typeof line === 'string') : [];
}

function addressesOf(value: unknown): readonly AddressView[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const lines = linesOf(row.lines);
    if (typeof row.addressUsageId !== 'string' || lines.length === 0) {
      return [];
    }
    return [
      {
        addressUsageId: row.addressUsageId,
        // Ohne Kennung steht die Verwendung fuer sich -- lieber eine Zeile zu
        // viel als zwei Anschriften unter einem Zweck.
        addressId: typeof row.addressId === 'string' && row.addressId.length > 0 ? row.addressId : row.addressUsageId,
        addressLockVersion: typeof row.addressLockVersion === 'number' ? row.addressLockVersion : 0,
        postalCode: typeof row.postalCode === 'string' ? row.postalCode : '',
        locality: typeof row.locality === 'string' ? row.locality : '',
        usageCode: typeof row.usageCode === 'string' ? row.usageCode : '',
        isPreferred: row.isPreferred === true,
        countryCode: typeof row.countryCode === 'string' ? row.countryCode : '',
        lines,
        normalizationStatus: typeof row.normalizationStatus === 'string' ? row.normalizationStatus : '',
        ...locationOf(row.locationStatus),
        sourceSystem: typeof row.sourceSystem === 'string' ? row.sourceSystem : '',
        validFrom: typeof row.validFrom === 'string' ? row.validFrom : '',
        lockVersion: typeof row.lockVersion === 'number' ? row.lockVersion : 0,
      },
    ];
  });
}

function endedAddressesOf(value: unknown): readonly EndedAddressView[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const lines = linesOf(row.lines);
    if (typeof row.addressUsageId !== 'string' || lines.length === 0) {
      return [];
    }
    return [
      {
        addressUsageId: row.addressUsageId,
        usageCode: typeof row.usageCode === 'string' ? row.usageCode : '',
        countryCode: typeof row.countryCode === 'string' ? row.countryCode : '',
        lines,
        endedAt: typeof row.endedAt === 'string' ? row.endedAt : '',
        ...(typeof row.endReasonCode === 'string' ? { endReasonCode: row.endReasonCode } : {}),
      },
    ];
  });
}

export interface ReadOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}

export async function fetchAddresses(options: ReadOptions): Promise<AddressesResult> {
  const result = await readResource({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/addresses`,
    accessToken: options.accessToken,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
  if (result.outcome !== 'loaded') {
    return result;
  }
  const body = result.body;
  return {
    outcome: 'loaded',
    data: {
      ...(typeof body.displayName === 'string' && body.displayName.length > 0 ? { displayName: body.displayName } : {}),
      postalAddresses: addressesOf(body.postalAddresses),
      endedPostalAddresses: endedAddressesOf(body.endedPostalAddresses),
      additionalPostalAddressCount:
        typeof body.additionalPostalAddressCount === 'number' ? body.additionalPostalAddressCount : 0,
      ...(typeof body.lifecycleStatus === 'string' && body.lifecycleStatus.length > 0 ? { lifecycleStatus: body.lifecycleStatus } : {}),
      endedPostalAddressesTruncated: body.endedPostalAddressesTruncated === true,
    },
  };
}

/** Wie der Befehls-Client die Antworten der Adresspflege in Saetze uebersetzt. */
const COMMAND_MESSAGES: CommandMessages = Object.freeze({
  notFound: NOT_FOUND_MESSAGE,
  conflictFallback: CONFLICT_MESSAGE,
  conflicts: Object.freeze({
    address_usage_already_ended: ALREADY_ENDED_MESSAGE,
    party_not_writable: NOT_WRITABLE_MESSAGE,
    address_outdated: ADDRESS_OUTDATED_MESSAGE,
    address_not_correctable: NOT_CORRECTABLE_MESSAGE,
    client_request_reused: REQUEST_REUSED_MESSAGE,
    client_request_superseded: REQUEST_SUPERSEDED_MESSAGE,
  }),
  unprocessable: Object.freeze({ end_reason_not_available: END_REASON_UNAVAILABLE_MESSAGE }),
});

function sendAddressCommand(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly path: string;
  readonly body: Record<string, unknown>;
  readonly messages?: CommandMessages;
  readonly send?: SendLike;
}): Promise<CommandResult> {
  return sendCommand({
    url: `${options.baseUrl}${options.path}`,
    accessToken: options.accessToken,
    body: options.body,
    messages: options.messages ?? COMMAND_MESSAGES,
    applied: (record) => ({
      usageCode: typeof record.usageCode === 'string' ? record.usageCode : '',
      lockVersion: typeof record.lockVersion === 'number' ? record.lockVersion : 0,
    }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export interface CommandOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}

export interface AddressInput {
  readonly usageCode: string;
  readonly countryCode: string;
  readonly addressLine1: string;
  readonly addressLine2: string;
  readonly postalCode: string;
  readonly locality: string;
}

/**
 * Leere optionale Felder gehen NICHT mit -- der Server liest eine fehlende
 * zweite Zeile als "keine", und eine leere Zeichenkette ist dieselbe Aussage
 * in einer Form, die er erst wegtrimmen muesste.
 *
 * `clientRequestId` ist Pflicht -- dieselbe Begruendung wie bei
 * `addContactPoint` (ADR-0060).
 */
export function addAddress(
  options: CommandOptions & { readonly input: AddressInput; readonly clientRequestId: string },
): Promise<CommandResult> {
  const { input } = options;
  return sendAddressCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/addresses`,
    body: {
      usageCode: input.usageCode,
      countryCode: input.countryCode,
      addressLine1: input.addressLine1,
      ...(input.addressLine2.trim().length === 0 ? {} : { addressLine2: input.addressLine2 }),
      ...(input.postalCode.trim().length === 0 ? {} : { postalCode: input.postalCode }),
      locality: input.locality,
      clientRequestId: options.clientRequestId,
    },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

/** "Auch verwenden als": dieselbe Anschrift fuer einen weiteren Zweck. */
export function addAddressUsage(
  options: CommandOptions & {
    readonly sourceAddressUsageId: string;
    readonly usageCode: string;
    readonly clientRequestId: string;
  },
): Promise<CommandResult> {
  return sendAddressCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/addresses/${encodeURIComponent(options.sourceAddressUsageId)}/usages`,
    body: { usageCode: options.usageCode, clientRequestId: options.clientRequestId },
    messages: {
      ...COMMAND_MESSAGES,
      conflicts: {
        ...COMMAND_MESSAGES.conflicts,
        address_usage_duplicate: duplicateUsageMessage(options.usageCode),
        client_request_superseded: USAGE_SUPERSEDED_MESSAGE,
      },
    },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export type CorrectionResult = CommandOutcome<{ readonly changed: boolean; readonly addressLockVersion: number }>;

/**
 * "Anschrift korrigieren": eine neue Fassung derselben Anschrift, fuer alle
 * ihre Zwecke. Ohne Kennung des Absendens -- eine Wiederholung derselben
 * Korrektur beantwortet der Server mit "war bereits so gespeichert".
 */
export function correctAddress(
  options: CommandOptions & {
    readonly addressUsageId: string;
    readonly input: Omit<AddressInput, 'usageCode'>;
    readonly addressLockVersion: number;
  },
): Promise<CorrectionResult> {
  const { input } = options;
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/addresses/${encodeURIComponent(options.addressUsageId)}/correction`,
    accessToken: options.accessToken,
    body: {
      countryCode: input.countryCode,
      addressLine1: input.addressLine1,
      ...(input.addressLine2.trim().length === 0 ? {} : { addressLine2: input.addressLine2 }),
      ...(input.postalCode.trim().length === 0 ? {} : { postalCode: input.postalCode }),
      locality: input.locality,
      addressLockVersion: options.addressLockVersion,
    },
    messages: COMMAND_MESSAGES,
    applied: (record) => ({
      changed: record.changed !== false,
      addressLockVersion: typeof record.addressLockVersion === 'number' ? record.addressLockVersion : 0,
    }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export function correctionConfirmation(changed: boolean, zwecke: number): string {
  if (!changed) {
    return 'Die Anschrift war bereits so gespeichert. Es wurde nichts geändert.';
  }
  return zwecke > 1
    ? 'Anschrift korrigiert. Alle Zwecke zeigen jetzt die neuen Angaben; die bisherigen werden nicht gelöscht.'
    : 'Anschrift korrigiert. Die bisherigen Angaben werden nicht gelöscht.';
}

export function endAddress(
  options: CommandOptions & { readonly addressUsageId: string; readonly endReasonCode: string; readonly lockVersion: number },
): Promise<CommandResult> {
  return sendAddressCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/addresses/${encodeURIComponent(options.addressUsageId)}/end`,
    body: { endReasonCode: options.endReasonCode, lockVersion: options.lockVersion },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

/**
 * Die Bestaetigung nennt die WIRKUNG und den RUECKWEG (CM-UX-004), nicht den
 * Vorgang.
 */
export function confirmationFor(operation: 'add' | 'add_usage' | 'end', usageCode: string): string {
  const zweck = usageLabel(usageCode);
  if (operation === 'add_usage') {
    return `Die Anschrift gilt jetzt auch für den Zweck „${zweck}“. Jeder Zweck lässt sich später einzeln wieder aufgeben.`;
  }
  if (operation === 'add') {
    return `${zweck} hinzugefügt. Sie gilt ab sofort.`;
  }
  return `${zweck} wird nicht mehr verwendet. Sie steht jetzt unter „Frühere Anschriften“ und ist dort weiter nachlesbar.`;
}

export function additionalAddressNotice(count: number): string | undefined {
  if (count <= 0) {
    return undefined;
  }
  return count === 1
    ? 'Eine weitere Anschrift passt nicht mehr in diese Ansicht. Setze nicht mehr benötigte auf „Nicht mehr verwenden“, dann erscheint sie.'
    : `${count} weitere Anschriften passen nicht mehr in diese Ansicht. Setze nicht mehr benötigte auf „Nicht mehr verwenden“, dann erscheinen sie.`;
}

/** Ohne Zahl -- der Server liefert keine. */
export const HISTORY_TRUNCATED_NOTICE = 'Es gibt weitere frühere Anschriften, die diese Ansicht nicht zeigt.';

/**
 * Der Satz zu einer abgewiesenen Eingabe, je Feld und Grund -- er sagt, WAS
 * zu aendern ist (NFR-UX-005). Der Rueckfall ist allgemein und ehrlich.
 */
export function validationMessageFor(fieldPath: string, reasonCode: string): string {
  if (reasonCode === 'field_too_long') {
    return 'Diese Angabe ist zu lang. Kürze sie oder verteile sie auf die zweite Adresszeile.';
  }
  if (reasonCode === 'field_malformed') {
    return 'Diese Angabe enthält Zeichen, die in einer Anschrift nicht vorkommen dürfen, etwa einen Zeilenumbruch.';
  }
  if (fieldPath === 'usageCode') {
    return 'Wähle, wofür diese Anschrift verwendet wird.';
  }
  if (fieldPath === 'countryCode') {
    return 'Wähle ein Land aus der Liste.';
  }
  if (fieldPath === 'addressLine1') {
    return 'Trage Straße und Hausnummer oder ein Postfach ein.';
  }
  if (fieldPath === 'postalCode') {
    return 'Für eine Anschrift in Deutschland ist die Postleitzahl nötig.';
  }
  if (fieldPath === 'locality') {
    return 'Trage den Ort ein.';
  }
  return 'Prüfe die markierte Angabe.';
}
